import { IStepOption } from "ngx-ui-tour-md-menu";
import { SaveButton } from "../../../utils/action";

export interface DesignerGuideStatus {
    hasCompleted: boolean;
}

export interface MyIStepOption extends IStepOption {
    stepDimensions?: MyStepDimensions;
}

export interface MyStepDimensions {
    width?: string;
    minWidth?: string;
    maxWidth?: string;
    height?: string;
    minHeight?: string;
    maxHeight?: string;
}

interface DesignerTourStepsType {
    compass: MyIStepOption;
    copy: MyIStepOption;
    paste: MyIStepOption;
    duplicate: MyIStepOption;
    undo: MyIStepOption;
    redo: MyIStepOption;
    deleteShape: MyIStepOption;
    zoomIn: MyIStepOption;
    zoomOut: MyIStepOption;
    shapeSelector: MyIStepOption;
    clickSelection: MyIStepOption;
    marqueeSelection: MyIStepOption;
    dragDrop: MyIStepOption;
    actions: MyIStepOption;
    flips: MyIStepOption;
    colourPicker: MyIStepOption;
    getCount: MyIStepOption;
    totalBlocks: MyIStepOption;
    saveButton: MyIStepOption;
    publishButton: MyIStepOption;
    loadButton: MyIStepOption;
    guide: MyIStepOption;
}

export const DesignerTourSteps: DesignerTourStepsType = {
    compass: {
        stepId: "compass-step",
        anchorId: "compass",
        content:
            "This cube acts as a compass, showing the current orientation of the 3D scene. It helps you navigate and understand the direction of your model.",
        title: "Compass",
    },
    copy: {
        stepId: "copy-step",
        anchorId: "copy",
        content: `
        <div class="demo-gif-container">
            <img class="demo-gif" src="assets/demo-gif/copy-paste-demo.gif" alt="Shape Selector Demo">
            <span class="click-hint">Click to enlarge</span>
        </div>

        <p class="shape-guide-description">
            You can copy one or multiple shapes to a target area on the canvas.
        </p>

        <div class="guide-steps">
            <p>To copy a shape, follow these steps:</p>

            <ol>
                <li>
                    <p><strong>Select the Shape(s)</strong></p>
                    <ul>
                        <li>
                            <p><strong>Method 1: Click Selection</strong></p>
                            <ul> 
                                <li><span class="platform">Mac:</span> Hold <kbd>Cmd (⌘)</kbd> + Click on a shape to select.</li> 
                                <li><span class="platform">Windows:</span> Hold <kbd>Ctrl</kbd> + Click on a shape to select.</li>
                            </ul>
                        </li>
                        <li>
                            <p><strong>Method 2: Drag Selection</strong></p>
                            <ul>
                                <li><span class="platform">Mac:</span> Hold <kbd>Option (⌥)</kbd> + <kbd>Shift</kbd> + Click and drag to create a selection box around the shapes you want to select.</li>
                                <li><span class="platform">Windows:</span> Hold <kbd>Alt</kbd> + <kbd>Shift</kbd> + Click and drag to create a selection box around the shapes you want to select.</li>
                            </ul>
                        </li>
                    </ul>
                </li>
                <li>
                    <p><strong>Copy the Shape(s)</strong></p>
                    <ul>
                        <li><span class="platform">Mac:</span> Press <kbd>Cmd (⌘)</kbd> + <kbd>C</kbd> (OR) click on <strong>Copy</strong> icon to copy the selected shape(s).</li>
                        <li><span class="platform">Windows:</span> Press <kbd>Ctrl</kbd> + <kbd>C</kbd> (OR) click on <strong>Copy</strong> icon to copy the selected shape(s).</li>
                    </ul>
                </li>
            </ol>
         <strong>Note: </strong>
            <span> When a copy-paste action is in progress, no other actions such as flipping or colour changes can’t be performed.
                    To perform a different action, you must first cancel the ongoing copy-paste by pressing the <kbd>Esc</kbd> key.
            </span>
        </div> 
        `,
        title: "Copy Shapes",
        stepDimensions: {
            width: "700px",
            height: "700px",
            maxWidth: "700px",
        },
        showArrow: true,
    },
    paste: {
        stepId: "paste-step",
        anchorId: "paste",
        content: `
        <div class="demo-gif-container">
            <img class="demo-gif" src="assets/demo-gif/copy-paste-demo.gif" alt="Shape Selector Demo">
            <span class="click-hint">Click to enlarge</span>
        </div>

        <p class="shape-guide-description">
        You can paste shapes that you've copied into the target area on the canvas.
        </p>

        <div class="guide-steps">
            <p>To paste a shape, follow these steps:</p>
            <ol>
            <li>
                    <p><strong>Set the Target Area</strong></p>
                    <ul>
                        <li><span class="platform">Mac:</span> Hold <kbd>Cmd (⌘)</kbd> + <kbd>Shift</kbd> and click on any mesh or plane to set the target area.</li>
                        <li><span class="platform">Windows:</span> Hold <kbd>Ctrl</kbd> + <kbd>Shift</kbd> and click on any mesh or plane to set the target area.</li>
                        <li>The copied shape(s) will paste relative to the target area.</li>
                    </ul>
                    </li>
                    <li>
                    <p><strong>Paste the Shape(s)</strong></p>
                    <ul>
                        <li><span class="platform">Mac:</span> Press <kbd>Cmd (⌘)</kbd> + <kbd>V</kbd> (OR) click on <strong>Paste</strong> icon to paste the copied shape(s) onto the target area.</li>
                        <li><span class="platform">Windows:</span> Press <kbd>Ctrl</kbd> + <kbd>V</kbd> (OR) click on <strong>Paste</strong> icon to paste the copied shape(s) onto the target area.</li>
                    </ul>
                </li>
            </ol>
            <strong>Note: </strong>
            <span> When a copy-paste action is in progress, no other actions such as flipping or colour changes can’t be performed.
                    To perform a different action, you must first cancel the ongoing copy-paste by pressing the <kbd>Esc</kbd> key.
            </span>
        </div>
        `,
        title: "Paste Shapes",
        stepDimensions: {
            width: "700px",
            height: "700px",
            maxWidth: "700px",
        },
        showArrow: true,
    },
    zoomIn: {
        stepId: "zoomIn-step",
        anchorId: "zoomIn",
        content: `
        <div class="demo-gif-container">
            <img class="demo-gif" src="assets/demo-gif/zoom-in-demo.gif" alt="Zoom In Demo">
            <span class="click-hint">Click to enlarge</span>
        </div>

        <p class="zoom-guide-description">
            The <strong>Zoom In</strong> function allows you to focus on specific details of your 3D scene.
        </p>

        <div class="guide-steps">
            <p>To zoom in, follow these steps:</p>

            <ol>
                <li>
                    <p><strong>Use the Zoom In Icon</strong></p>
                    <ul>
                        <li>Click the <strong>Zoom In</strong> icon.</li>
                    </ul>
                </li>
                <li>
                    <p><strong>Use the Scroll Wheel</strong></p>
                    <ul>
                        <li>Scroll up using your mouse wheel to zoom in.</li>
                        <li>Scroll down to zoom back out.</li>
                    </ul>
                </li>
                <li>
                    <p><strong>Focus on Specific Areas</strong></p>
                    <ul>
                        <li>Zooming in helps with detailed adjustments to your objects.</li>
                        <li>Combine zooming with panning for better control.</li>
                    </ul>
                </li>
                <li>
                    <p><strong>Pan to Adjust Your View</strong></p>
                    <ul>
                        <li><strong>Option 1:</strong> Hold <kbd>Shift</kbd> + click and drag to pan.</li>
                        <li><strong>Option 2:</strong> Simply right-click and drag to move around.</li>
                    </ul>
                </li>
            </ol>
        </div>
        `,
        title: "Zoom In",
        stepDimensions: { width: "calc(50vw - 20px)", maxWidth: "700px" },
        placement: {
            xPosition: "before",
            yPosition: "below",
            horizontal: false,
        },
    },
    zoomOut: {
        stepId: "zoomOut-step",
        anchorId: "zoomOut",
        content: `
        <div class="demo-gif-container">
            <img class="demo-gif" src="assets/demo-gif/zoom-out-demo.gif" alt="Zoom Out Demo">
            <span class="click-hint">Click to enlarge</span>
        </div>

        <p class="zoom-guide-description">
            The <strong>Zoom Out</strong> function allows you to get a wider view of your 3D scene, making it easier to navigate and position objects.
        </p>

        <div class="guide-steps">
            <p>To zoom out, follow these steps:</p>

            <ol>
                <li>
                    <p><strong>Use the Zoom Out Icon</strong></p>
                    <ul>
                        <li>Click the <strong>Zoom Out</strong> icon.</li>
                    </ul>
                </li>
                <li>
                    <p><strong>Use the Scroll Wheel</strong></p>
                    <ul>
                        <li>Scroll down using your mouse wheel to zoom out.</li>
                        <li>Scroll up to zoom back in.</li>
                    </ul>
                </li>
                <li>
                    <p><strong>Adjust for Better View</strong></p>
                    <ul>
                        <li>Zooming out helps you position objects more accurately in your scene.</li>
                        <li>Combine zoom with panning for precise navigation.</li>
                    </ul>
                </li>
                <li>
                    <p><strong>Pan to Adjust Your View</strong></p>
                    <ul>
                        <li><strong>Option 1:</strong> Hold <kbd>Shift</kbd> + click and drag to pan.</li>
                        <li><strong>Option 2:</strong> Simply right-click and drag to move around.</li>
                    </ul>
                </li>
            </ol>
        </div>
        `,
        title: "Zoom Out",
        stepDimensions: { width: "calc(50vw - 20px)", maxWidth: "700px" },
        placement: {
            xPosition: "before",
            yPosition: "below",
            horizontal: false,
        },
    },
    shapeSelector: {
        stepId: "shapeSelector-step",
        anchorId: "shapeSelector",
        content: `
        <div class="demo-gif-container">
            <img class="demo-gif" src="assets/demo-gif/generate-mesh-demo.gif" alt="Shape Selector Demo">
            <span class="click-hint">Click to enlarge</span>
        </div>

        <p class="shape-guide-description">
            The <strong>Shape Selector</strong> allows you to choose different 3D shapes to add to your scene effortlessly.
        </p>

        <div class="guide-steps">
            <p>To use the Shape Selector, follow these steps:</p>

            <ol>
                <li>
                    <p><strong>Choose a Shape</strong></p>
                    <ul>
                        <li> Click on a shape from the selector like <strong>Cube, Cylinder, Cone, and Prism</strong>.</li>
                        <li>The selected shape will be highlighted.</li>
                    </ul>
                </li>
                <li>
                    <p><strong>Place the Shape</strong></p>
                    <ul>
                        <li>
                            Double-click on any grid point of the plane to place the shape.
                        </li>
                        <li>
                            Alternatively, double-click on the face of an existing shape to attach the new shape to it.
                        </li>
                        <li>
                            The shape will be positioned based on the clicked surface.
                        </li>
                    </ul>
                </li>
            </ol>
        </div>
        `,
        title: "Shape Selector",
        stepDimensions: { width: "calc(50vw - 20px)", maxWidth: "700px" },
        placement: {
            xPosition: "before",
            yPosition: "below",
            horizontal: true,
        },
    },
    clickSelection: {
        stepId: "clickSelection-step",
        anchorId: "centeredTourStep",
        content: `
        <div class="demo-gif-container">
            <img class="demo-gif" src="assets/demo-gif/click-selection-demo.gif" alt="Shape Selector Demo">
            <span class="click-hint">Click to enlarge</span>
        </div>

        <p class="shape-guide-description">
            The <strong>Click Selection</strong> allows you to select multiple shapes individually by holding a key and clicking on them.
        </p>

        <div class="guide-steps">
            <p>To perform Click Selection, follow these steps:</p>

            <ol>
                <li>
                    <p><strong>Select a Shape</strong></p>
                    <ul>
                        <li> Hold <kbd>Cmd (⌘)</kbd> (Mac) or <kbd>Ctrl</kbd> (Windows) and click on any shape to select it.</li>
                        <li> You can click on multiple shapes one by one to add them to your selection.</li>
                    </ul>
                </li>
                <li>
                    <p><strong>Deselect a Shape</strong></p>
                    <ul>
                        <li> To deselect a shape, hold <kbd>Cmd (⌘)</kbd> (Mac) or <kbd>Ctrl</kbd> (Windows) and click on the selected shape again.</li>
                    </ul>
                </li>
                <li>
                    <p><strong>Clear Selection</strong></p>
                    <ul>
                        <li>Click on any mesh or press <kbd>Esc</kbd> to clear all selections.</li>
                    </ul>
                </li>
                <li>
                    <p><strong>Perform Actions on Selected Shapes</strong></p>
                    <ul>
                        <li> Once shapes are selected, you can move, delete, or modify them as a group.</li>
                    </ul>
                </li>
            </ol>
        </div>
        `,
        title: "Click Selection",
        stepDimensions: {
            width: "700px",
            height: "700px",
            maxWidth: "700px",
        },
        showArrow: false,
    },
    marqueeSelection: {
        stepId: "marqueeSelection-step",
        anchorId: "centeredTourStep",
        content: `
        <div class="demo-gif-container">
            <img class="demo-gif" src="assets/demo-gif/marquee-selection-demo.gif" alt="Shape Selector Demo">
            <span class="click-hint">Click to enlarge</span>
        </div>

        <p class="shape-guide-description">
            The <strong>Marquee Selection</strong> allows you to select multiple shapes quickly by drawing a selection box around them.
        </p>

        <div class="guide-steps">
            <p>To perform Marquee Selection, follow these steps:</p>

            <ol>
                <li>
                    <p><strong>Activate Marquee Selection Mode</strong></p>
                    <ul>
                        <li> 
                            <span class="platform">Mac:</span> Press and hold the <kbd>Shift</kbd> and <kbd>Option (⌥)</kbd> keys on your keyboard.
                        </li>
                        <li> 
                            <span class="platform">Windows:</span> Press and hold the <kbd>Shift</kbd> and <kbd>Alt</kbd> keys on your keyboard.</li>
                        </li>
                    </ul>
                </li>
                <li>
                    <p><strong>Click and Drag to Select</strong></p>
                    <ul>
                        <li> While holding, click and drag anywhere on the <strong>canvas</strong> to draw a selection box.</li>
                        <li> All shapes that fall within the box will be selected.</li>
                        <li> You can also use this method to select multiple shapes at once.</li>
                    </ul>
                </li>
                <li>
                    <p><strong>Release to Finalize Selection</strong></p>
                    <ul>
                        <li> Once you release the mouse button, all shapes within the box will remain selected.</li>
                        <li> You can now move, delete, or modify them as a group.</li>
                    </ul>
                </li>
            </ol>
        </div>
        `,
        title: "Marquee Selection",
        stepDimensions: {
            width: "700px",
            height: "700px",
            maxWidth: "700px",
        },
        showArrow: false,
    },
    deleteShape: {
        stepId: "deleteShape-step",
        anchorId: "delete",
        content: `
        <div class="demo-gif-container">
            <img class="demo-gif" src="assets/demo-gif/delete-shape-demo.gif" alt="Shape Selector Demo">
            <span class="click-hint">Click to enlarge</span>
        </div>

        <p class="shape-guide-description">
            You can delete one or multiple shapes in two ways: by selecting individual shapes or using a selection box to group them.
        </p>

        <div class="guide-steps">
            <p>To delete a shape, follow these steps:</p>

            <ol>
                <li>
                    <p><strong>Select the Shape(s)</strong></p>
                    <ul>
                        <li>
                            <p><strong>Method 1: Click Selection</strong></p>
                            <ul> 
                                <li><span class="platform">Mac:</span> Hold <kbd>Cmd (⌘)</kbd> + Click on a shape to select.</li> 
                                <li><span class="platform">Windows:</span> Hold <kbd>Ctrl</kbd> + Click on a shape to select.</li>
                            </ul>
                        </li>
                        <li>
                            <p><strong>Method 2: Drag Selection</strong></p>
                            <ul>
                                <li><span class="platform">Mac:</span> Hold <kbd>Option (⌥)</kbd> + <kbd>Shift</kbd> + Click and drag to create a selection box around the shapes you want to select.</li>
                                <li><span class="platform">Windows:</span> Hold <kbd>Alt</kbd> + <kbd>Shift</kbd> + Click and drag to create a selection box around the shapes you want to select.</li>
                            </ul>
                        </li>
                    </ul>
                </li>
                <li>
                    <p><strong>Delete the Selected Shape(s)</strong></p>
                    <ul>
                        <li>
                            <span class="platform">Mac:</span> Press the <kbd>Delete</kbd> key on your keyboard (OR) click on <strong>Delete</strong> icon to remove the selected shape(s).
                        </li>
                        <li>
                            <span class="platform">Windows:</span> Press the <kbd>Delete</kbd> or <kbd>Backspace</kbd> key on your keyboard (OR) click on <strong>Delete</strong> icon to remove the selected shape(s).
                        </li>
                        <li>
                            If you mistakenly delete a shape, you can undo the action (<kbd>Cmd (⌘)</kbd> + <kbd>Z</kbd> on Mac or <kbd>Ctrl</kbd> + <kbd>Z</kbd> on Windows) (OR) click on <strong>Undo</strong> icon to restore it.
                        </li>
                        <p><b>Note</b>: Undo/redo is not available for selection and deselection actions.</p>
                    </ul>
                </li>
            </ol>
        </div>
        `,
        title: "Delete Shape",
        stepDimensions: {
            width: "700px",
            height: "700px",
            maxWidth: "700px",
        },
        showArrow: true,
    },

    duplicate: {
        stepId: "duplicate-step",
        anchorId: "duplicate",
        content: `
        <div class="demo-gif-container">
            <img class="demo-gif" src="assets/demo-gif/duplicate-shapes.mov.gif" alt="Shape Selector Demo">
            <span class="click-hint">Click to enlarge</span>
        </div>

        <p class="shape-guide-description">
            You can duplicate one or more shapes.
        </p>

        <div class="guide-steps">
            <p>To duplicate shape, follow these steps:</p>
                    <ul>
                        <li>
                            <p><strong>Step 1: Click Selection</strong></p>
                            <ul> 
                                <li><span class="platform">Mac:</span> Hold <kbd>Cmd (⌘)</kbd> + Click on a shape to select.</li> 
                                <li><span class="platform">Windows:</span> Hold <kbd>Ctrl</kbd> + Click on a shape to select.</li>
                            </ul>
                        </li>
                         <li>
                            <p><strong>Step 2: Duplicate</strong></p>
                            <ul>
                                <li>
                                <span class="platform">Mac:</span>
                                Press <kbd>Cmd (⌘)</kbd> + <kbd>D</kbd> (OR) click on <strong>Duplicate</strong> icon to duplicate the selected shape(s).
                                </li>
                                <li>
                                <span class="platform">Windows:</span>
                                Press <kbd>Ctrl(⌘)</kbd> + <kbd>D</kbd> (OR) click on <strong>Duplicate</strong> icon to duplicate the selected shape(s).
                                </li>
                            </ul>
                        </li>
                        <li>
                            <p><strong>Step 3: Move the duplicated shape </strong></p>
                            <ul>
                                <li>The duplicated shape will appear at the same position as the original. You can then drag it to a new location.</li>
                            </ul>
                        </li>
                    </ul>   
        </div>
        `,
        title: "Duplicate Shape",
        stepDimensions: {
            width: "700px",
            height: "700px",
            maxWidth: "700px",
        },
        showArrow: true,
    },

    dragDrop: {
        stepId: "dragDrop-step",
        anchorId: "centeredTourStep",
        content: `
        <div class="demo-gif-container">
            <img class="demo-gif" src="assets/demo-gif/drag-drop-demo.gif" alt="Shape Selector Demo">
            <span class="click-hint">Click to enlarge</span>
        </div>

        <p class="shape-guide-description">
            You can move shapes on the canvas using Drag and Drop.  
            Right-click on a shape (or multiple selected shapes), then drag to reposition.
        </p>

        <div class="guide-steps">
            <p>Follow these steps to drag and drop shapes:</p>

            <ol>
                <li>
                    <p><strong>Dragging a Single Shape</strong></p>
                    <ul>
                        <li>Right-click on the shape to activate <strong>Drag Mode</strong>.</li>
                        <li>Move the mouse to reposition the shape.</li>
                        <li>Left-click to drop the shape in the new location.</li>
                    </ul>
                </li>
                <li>
                    <p><strong>Dragging Multiple Shapes</strong></p>
                    <p>First, select multiple shapes, then activate Drag Mode:</p>
                    <ul>
                        <li>
                            <p><strong>Method 1: Click Selection</strong></p>
                            <ul> 
                                <li><span class="platform">Mac:</span> Hold <kbd>Cmd (⌘)</kbd> + Click on multiple shapes.</li> 
                                <li><span class="platform">Windows:</span> Hold <kbd>Ctrl</kbd> + Click on multiple shapes.</li>
                            </ul>
                        </li>
                        <li>
                            <p><strong>Method 2: Marquee Selection</strong></p>
                            <ul>
                                <li><span class="platform">Mac:</span> Hold <kbd>Option (⌥)</kbd> + <kbd>Shift</kbd> and drag to create a selection box.</li>
                                <li><span class="platform">Windows:</span> Hold <kbd>Alt</kbd> + <kbd>Shift</kbd> and drag to create a selection box.</li>
                            </ul>
                        </li>
                        <li>Once shapes are selected, right-click anywhere on the canvas to activate <strong>Drag Mode</strong>.</li>
                        <li>Move the mouse to reposition the selected shapes.</li>
                        <li>Left-click to drop them in the new location.</li>
                    </ul>
                </li>
            </ol>
        </div>
        `,
        title: "Drag and Drop",
        stepDimensions: {
            width: "700px",
            height: "700px",
            maxWidth: "700px",
        },
        showArrow: false,
    },
    undo: {
        stepId: "undo-step",
        anchorId: "undo",
        content: `
        <div class="demo-gif-container">
            <img class="demo-gif" src="assets/demo-gif/undo-new.gif" alt="Shape Selector Demo">
            <span class="click-hint">Click to enlarge</span>
        </div>

        <p class="shape-guide-description">
            You can undo actions to revert changes made on the canvas. 
            The system supports up to 5 undo histories.
        </p>

        <div class="guide-steps">
            <p>To undo an action, follow this step:</p>
            <ol>
                <li>
                    <p><strong>Undo an Action</strong></p>
                    <ul>
                        <li>
                            <span class="platform">Mac:</span> Press <kbd>Cmd (⌘)</kbd> + <kbd>Z</kbd> (OR) click on <strong>Undo</strong> icon to undo the last action.
                        </li>
                        <li>
                            <span class="platform">Windows:</span> Press <kbd>Ctrl</kbd> + <kbd>Z</kbd> (OR) click on <strong>Undo</strong> icon to undo the last action.
                        </li>
                    </ul>
                </li>
        </div>
        `,
        title: "Undo Actions",
        stepDimensions: {
            width: "700px",
            height: "620px",
            maxWidth: "700px",
        },
        showArrow: true,
    },
    redo: {
        stepId: "redo-step",
        anchorId: "redo",
        content: `
        <div class="demo-gif-container">
            <img class="demo-gif" src="assets/demo-gif/redo-new.gif" alt="Shape Selector Demo">
            <span class="click-hint">Click to enlarge</span>
        </div>

        <p class="shape-guide-description">
            You can redo actions to reapply changes made on the canvas. 
            The system supports up to 5 redo histories.
        </p>

        <div class="guide-steps">
            <p>To redo an action, follow this step:</p>
            <ol>
                <li>
                    <p><strong>Redo an Action</strong></p>
                    <ul>
                        <li>
                            <span class="platform">Mac:</span> Press <kbd>Cmd (⌘)</kbd> + <kbd>Shift</kbd> + <kbd>Z</kbd> (OR) click on <strong>Redo</strong> icon to redo the last undone action.
                        </li>
                        <li>
                            <span class="platform">Windows:</span> Press <kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>Z</kbd> (OR) click on <strong>Redo</strong> icon to redo the last undone action.
                        </li>
                    </ul>
                </li>
            </ol>
        </div>
        `,
        title: "Redo Actions",
        stepDimensions: {
            width: "700px",
            height: "620px",
            maxWidth: "700px",
        },
        showArrow: true,
    },
    guide: {
        stepId: "guide-step",
        anchorId: "guide",
        content: `
            <p>Clicking this button will restart the onboarding tour, 
            even if you have already completed it before.
            </p>
            <p>You can use this to revisit the step-by-step guide 
            and explore the platform's features again.</p>
        `,
        title: "Guide",
        placement: {
            xPosition: "before",
            yPosition: "below",
        },
    },
    actions: {
        stepId: "actions-step",
        anchorId: "actions",
        title: "Actions",
        content: `
            By clicking this button, you will open the Actions Menu, which provides additional tools for modifying your design.
        `,
        placement: {
            xPosition: "before",
            yPosition: "below",
        },
    },
    flips: {
        stepId: "flips-step",
        anchorId: "flips",
        title: "Flip Tools",
        content: `
            <div class="demo-gif-container">
                <img class="demo-gif" src="assets/demo-gif/flips-demo.gif" alt="Flips Demo">
                <span class="click-hint">Click to enlarge</span>
            </div>
            
            <p class="flip-guide-description">
                The <strong>Flip Tools</strong> allow you to flip meshes in your 3D scene, helping you achieve precise positioning of elements.
            </p>

            <div class="guide-steps">
                <p>To use the Flip Tools, follow these steps:</p>

                <ol>
                    <li>
                        <p><strong>Select the Shape(s)</strong></p>
                        <ul>
                            <li>
                                <p><strong>Method 1: Click Selection</strong></p>
                                <ul> 
                                    <li><span class="platform">Mac:</span> Hold <kbd>Cmd (⌘)</kbd> + Click on a shape to select.</li> 
                                    <li><span class="platform">Windows:</span> Hold <kbd>Ctrl</kbd> + Click on a shape to select.</li>
                                </ul>
                            </li>
                            <li>
                                <p><strong>Method 2: Drag Selection</strong></p>
                                <ul>
                                    <li><span class="platform">Mac:</span> Hold <kbd>Option (⌥)</kbd> + <kbd>Shift</kbd> + Click and drag to create a selection box around the shapes you want to select.</li>
                                    <li><span class="platform">Windows:</span> Hold <kbd>Alt</kbd> + <kbd>Shift</kbd> + Click and drag to create a selection box around the shapes you want to select.</li>
                                </ul>
                            </li>
                        </ul>
                    </li>
                    <li>
                        <p><strong>Apply a Flip Action</strong></p>
                        <ul>
                            <li>Navigate to the <strong>Flip Tools</strong> panel.</li>
                            <li>Click on any available flip action to flip the selected mesh along the desired axis.</li>
                        </ul>
                    </li>
                </ol>
            </div>
        `,
        stepDimensions: { width: "calc(50vw - 20px)", maxWidth: "700px", maxHeight: "480px" },
        placement: {
            xPosition: "before",
            yPosition: "below",
            horizontal: true,
        },
    },
    colourPicker: {
        stepId: "colourPicker-step",
        anchorId: "colorPicker",
        title: "Colour Picker",
        content: `
            <div class="demo-gif-container">
                <img class="demo-gif" src="assets/demo-gif/color-change.gif" alt="Colour Change Demo">
                <span class="click-hint">Click to enlarge</span>
            </div>

            <p class="color-guide-description">
                The <strong>Colour Picker</strong> allows you to apply colours to selected shapes, either to individual faces or the entire shape.
            </p>

            <div class="guide-steps">
                <p>To change the colour of a shape, follow these steps:</p>

                <ol>
                    <li>
                        <p><strong>Select the Shape(s)</strong></p>
                        <ul>
                            <li>
                                <p><strong>Method 1: Click Selection</strong></p>
                                <ul> 
                                    <li><span class="platform">Mac:</span> Hold <kbd>Cmd (⌘)</kbd> + Click on a shape to select.</li> 
                                    <li><span class="platform">Windows:</span> Hold <kbd>Ctrl</kbd> + Click on a shape to select.</li>
                                </ul>
                            </li>
                            <li>
                                <p><strong>Method 2: Drag Selection</strong></p>
                                <ul>
                                    <li><span class="platform">Mac:</span> Hold <kbd>Option (⌥)</kbd> + <kbd>Shift</kbd> + Click and drag to create a selection box around the shapes you want to select.</li>
                                    <li><span class="platform">Windows:</span> Hold <kbd>Alt</kbd> + <kbd>Shift</kbd> + Click and drag to create a selection box around the shapes you want to select.</li>
                                </ul>
                            </li>
                        </ul>
                    </li>
                    <li>
                        <p><strong>Select a Colour</strong></p>
                        <ul>
                            <li>Click on your desired colour from the palette.</li>
                        </ul>
                    </li>
                    <li>
                        <p><strong>Choose the Colouring Mode</strong></p>
                        <ul>
                            <li>A pop-up will appear asking how you want to apply the colour:</li>
                            <li><strong>Single Face:</strong> Applies colour only to the face where the cursor was when selecting the shape, including with marquee selection.</li>
                            <li><strong>Full Face:</strong> Applies colour to the entire selected shape.</li>
                        </ul>
                    </li>
                </ol>
            </div>
        `,
        stepDimensions: { width: "calc(50vw - 20px)", maxWidth: "700px", maxHeight: "480px" },
        placement: {
            xPosition: "before",
            yPosition: "above",
            horizontal: true,
        },
    },
    getCount: {
        stepId: "getCount-step",
        anchorId: "getCount",
        title: "Get Count",
        content: `
            The Get Count feature allows you to get details of blocks in your design.
        `,
        placement: {
            xPosition: "before",
            yPosition: "above",
            horizontal: true,
        },
    },
    totalBlocks: {
        stepId: "totalBlocks-step",
        anchorId: "totalBlocks",
        title: "Total Blocks",
        content: `
            The Total Blocks feature displays the total number of blocks in your design.
        `,
        placement: {
            xPosition: "before",
            yPosition: "above",
            horizontal: true,
        },
    },
    saveButton: {
        stepId: "saveButton-step",
        anchorId: SaveButton.saveStructureIconLabel,
        title: "Save Button",
        content: `
                    Click this button to save your current design progress.
                    <br><br>
                    If you make any changes later, this button will change to an <b>Update</b> button, 
                    allowing you to save the modified structure.
                    <br><br>
                    This ensures that your work is stored and can be resumed later.
                `,
        placement: {
            xPosition: "before",
            yPosition: "above",
        },
    },
    publishButton: {
        stepId: "publishButton-step",
        anchorId: SaveButton.publishIconLabel,
        title: "Publish Button",
        content: `
                    Click this button to submit your 3D structure for review.
                    <br><br>
                    Once submitted, Admins will review your structure. If approved, it will be listed as a product in the app.
                    <br><br>
                    Whenever someone purchases your design, you will receive a reward!
                `,
        placement: {
            xPosition: "before",
            yPosition: "above",
        },
    },
    loadButton: {
        stepId: "loadButton-step",
        anchorId: SaveButton.loadIconLabel,
        title: "Load Button",
        content: `
                    Click this button to view all your save 3D structure.
                    <br><br>
                    After clicking, your selected structure will appear on the canvas screen.
                    <br><br>
                    Additionally, you can customize your structure to match your preferred design.
                `,
        placement: {
            xPosition: "before",
            yPosition: "above",
        },
    },
};
